import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  CollectionReference,
  doc,
  Firestore,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { PathConstants } from '../../db.paths';
import { Note } from '../models/note.model'; // Assuming Note interface is here
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class NotesService {
  private firestore: Firestore = inject(Firestore);
  private authService: AuthService = inject(AuthService);

  constructor() { }

  getCaseNotes(caseId: string): Observable<Note[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap(user => {
        if (!user) {
          return of([]);
        }
        const notesPath = PathConstants.getNotesCollectionPath(user.uid, caseId);
        const notesCollectionRef = collection(this.firestore, notesPath) as CollectionReference<Note>;
        const q = query(notesCollectionRef, where('deleted', '==', false));

        return collectionData<Note>(q, { idField: 'id' }).pipe(
          map(notes => notes.map(note => this._convertTimestampsToDates(note)))
        );
      })
    );
  }

  addNote(caseId: string, noteContent: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService.getLoggedInUser().pipe(take(1)).subscribe(user => {
        if (!user) {
          return reject('User not logged in');
        }
        const notesPath = PathConstants.getNotesCollectionPath(user.uid, caseId);
        const notesCollectionRef = collection(this.firestore, notesPath);

        const newNote: Partial<Note> = {
          content: noteContent,
          authorName: user.displayName || 'Unknown User', // Added back
          createdAt: serverTimestamp() as Timestamp,
          updatedAt: serverTimestamp() as Timestamp,
          deleted: false
        };

        addDoc(notesCollectionRef, newNote)
          .then(() => resolve())
          .catch(error => reject(error));
      });
    });
  }

  updateNote(caseId: string, noteId: string, newContent: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService.getLoggedInUser().pipe(take(1)).subscribe(user => {
        if (!user) {
          return reject('User not logged in');
        }
        const notePath = PathConstants.getNotePath(user.uid, caseId, noteId);
        const noteRef = doc(this.firestore, notePath);

        updateDoc(noteRef, {
          content: newContent,
          updatedAt: serverTimestamp()
        })
          .then(() => resolve())
          .catch(error => reject(error));
      });
    });
  }

  deleteNote(caseId: string, noteId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService.getLoggedInUser().pipe(take(1)).subscribe(user => {
        if (!user) {
          return reject('User not logged in');
        }
        const notePath = PathConstants.getNotePath(user.uid, caseId, noteId);
        const noteRef = doc(this.firestore, notePath);

        updateDoc(noteRef, {
          deleted: true,
          updatedAt: serverTimestamp()
        })
          .then(() => resolve())
          .catch(error => reject(error));
      });
    });
  }

  private _convertTimestampsToDates(note: Note): Note {
    const convertedNote = { ...note };
    if (convertedNote.createdAt && typeof (convertedNote.createdAt as any).toDate === 'function') {
      convertedNote.createdAt = (convertedNote.createdAt as any).toDate();
    }
    if (convertedNote.updatedAt && typeof (convertedNote.updatedAt as any).toDate === 'function') {
      convertedNote.updatedAt = (convertedNote.updatedAt as any).toDate();
    }
    return convertedNote;
  }
}